import { IfAbility } from '@efdigital/ll-addon-auth';
import {
  CohortNavTemplate,
  ProgramNavTemplate,
} from '@efdigital/ll-addon-content';
import AdminLegacyWarning from '@efdigital/ll-admin-v2/components/admin-legacy-warning.gts';
import { EditContext } from '@efdigital/ll-admin-v2/components/edit-context.gts';
import { programTemplateParts } from '@efdigital/ll-admin-v2/components/parts.gts';
import ContentBreadcrumb from '@efdigital/ll-admin-v2/components/programs/content-breadcrumb.gts';
import { EditCohortCertificates } from '@efdigital/ll-admin-v2/components/programs/edit-cohort-certificates.gts';
import { EditCohortContacts } from '@efdigital/ll-admin-v2/components/programs/edit-cohort-contacts.gts';
import { EditCohortModules } from '@efdigital/ll-admin-v2/components/programs/edit-cohort-modules.gts';
import { EditCohortPages } from '@efdigital/ll-admin-v2/components/programs/edit-cohort-pages.gts';
import { EditCohortQuizzes } from '@efdigital/ll-admin-v2/components/programs/edit-cohort-quizzes.gts';
import { EditCohortVisibility } from '@efdigital/ll-admin-v2/components/programs/edit-cohort-visibility.gts';
import { Sidebar } from '@efdigital/ll-admin-v2/components/sidebar/sidebar.gts';
import type { ModelFrom } from '@efdigital/ll-admin-v2/lib/type-utils';
import type ProgramsProgramCohortContentModuleRoute from '@efdigital/ll-admin-v2/routes/programs/program/cohort/content/module';
import type AbilityService from '@efdigital/ll-admin-v2/services/ability';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { pageTitle } from 'ember-page-title';

interface ProgramsProgramCohortContentIndexSignature {
  Args: {
    model: ModelFrom<ProgramsProgramCohortContentModuleRoute>;
    controller: unknown;
  };
}

const parts = programTemplateParts;

class Template extends Component<ProgramsProgramCohortContentIndexSignature> {
  @service declare readonly ability: AbilityService;

  <template>
    {{pageTitle "Cohort"}}

    <ContentBreadcrumb @model={{@model}} />

    <div class="mb-5">
      <EditContext as |context|>
        <Sidebar>
          <:content>
            <AdminLegacyWarning
              @item={{@model.cohort}}
              @itemType="cohort"
              @programId={{@model.program.id}}
              @cohortId={{@model.cohort.id}}
              class="container"
            />

            <ProgramNavTemplate
              @program={{@model.program}}
              @cohort={{@model.cohort}}
              @parts={{parts}}
            />
            <CohortNavTemplate
              @program={{@model.program}}
              @cohort={{@model.cohort}}
              @parts={{parts}}
            >
              <div class="alert alert-secondary p-5">
                <h1 class="display-6">This is the cohort editor.</h1>
                <ul class="lead mb-0">
                  <li>Use the panel on the right to edit the cohort’s structure.</li>
                  <li>Navigate to a page, quiz, or module to view or edit its
                    contents.</li>
                  <li>Participants will start on the first page when accessing
                    the cohort.</li>
                </ul>
              </div>
            </CohortNavTemplate>
          </:content>
          <:sidebar>
            <IfAbility @ability="content" @atLeast="editor">
              <:if>
                <div class="px-3 py-4">
                  <EditCohortPages
                    @item={{@model.cohort}}
                    @context={{context}}
                    @params={{@model}}
                    class="mb-5"
                  />

                  <EditCohortModules
                    @item={{@model.cohort}}
                    @context={{context}}
                    @params={{@model}}
                    class="mb-5"
                  />

                  <EditCohortQuizzes
                    @item={{@model.cohort}}
                    @context={{context}}
                    @params={{@model}}
                    class="mb-5"
                  />

                  <EditCohortCertificates
                    @item={{@model.cohort}}
                    @context={{context}}
                    @params={{@model}}
                    class="mb-5"
                  />

                  <EditCohortContacts
                    @item={{@model.cohort}}
                    @context={{context}}
                    class="mb-5"
                  />

                  <EditCohortVisibility
                    @item={{@model.cohort}}
                    @context={{context}}
                    class="mb-5"
                  />
                </div>
              </:if>
              <:else>
                <div class="px-3 py-4">
                  <div class="alert alert-info" role="alert">
                    <h4 class="alert-heading">View Only</h4>
                    <p>You have view-only access to content.</p>
                    <p class="mb-0">To edit content structure, you need the
                      "Edit pages" and "Edit resources" permissions.</p>
                  </div>
                </div>
              </:else>
            </IfAbility>
          </:sidebar>
        </Sidebar>
      </EditContext>
    </div>
  </template>
}

export default Template;
